
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const faqData = [
    {
      category: 'Getting Started',
      questions: [
        {
          question: 'How do I create an account?',
          answer: 'You can create an account by clicking the "Join Now" button and filling out the registration form. You must be 18 years or older to register.'
        },
        {
          question: 'What games can I play?',
          answer: 'GoldenTicketLucky offers PowerBall, Oz Lotto, and Saturday Lotto. Each game has different prize structures and draw times.'
        },
        {
          question: 'How do I buy a ticket?',
          answer: 'After logging in, go to the Play section, select your numbers (or use Quick Pick), choose your bet amount, and confirm your purchase.'
        }
      ]
    },
    {
      category: 'Payments & Winnings',
      questions: [
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept major credit cards, debit cards, PayPal, and bank transfers. All transactions are secure and encrypted.'
        },
        {
          question: 'How are winnings paid out?',
          answer: 'Winnings under $1,000 are automatically credited to your account. Larger prizes require identity verification and are paid by bank transfer.'
        },
        {
          question: 'How long does it take to receive winnings?',
          answer: 'Small prizes are instant. Major prizes typically take 3-5 business days after verification is complete.'
        }
      ]
    },
    {
      category: 'Responsible Gaming',
      questions: [
        {
          question: 'Can I set spending limits?',
          answer: 'Yes, you can set daily, weekly, or monthly spending limits in your account settings. These limits help you play responsibly.'
        },
        {
          question: 'What if I need help with gambling problems?',
          answer: 'We provide links to gambling support services and offer self-exclusion options. Contact our support team for assistance.'
        },
        {
          question: 'How does Quick Pick work?',
          answer: 'Quick Pick randomly selects numbers for you using a certified random number generator, giving you the same chances as manually selected numbers.'
        }
      ]
    },
    {
      category: 'Technical Support',
      questions: [
        {
          question: 'What if the website is not working?',
          answer: 'Try refreshing the page or clearing your browser cache. If problems persist, contact our 24/7 support team.'
        },
        {
          question: 'Can I play on mobile devices?',
          answer: 'Yes, our website is fully mobile-responsive and works on all smartphones and tablets.'
        },
        {
          question: 'Is my personal information secure?',
          answer: 'Absolutely. We use bank-level encryption and comply with Australian privacy laws to protect your data.'
        }
      ]
    }
  ];

  const filteredFAQ = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
           q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-yellow-500 to-yellow-300 py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-gray-700 mb-8">Find answers to common questions about GoldenTicketLucky</p>
          
          <div className="max-w-md mx-auto">
            <Input
              type="text"
              placeholder="Search FAQ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-white"
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          {filteredFAQ.length > 0 ? (
            <div className="space-y-8">
              {filteredFAQ.map((category, categoryIndex) => (
                <Card key={categoryIndex}>
                  <CardHeader>
                    <CardTitle className="text-2xl text-yellow-600">{category.category}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Accordion type="single" collapsible className="w-full">
                      {category.questions.map((faq, faqIndex) => (
                        <AccordionItem key={faqIndex} value={`item-${categoryIndex}-${faqIndex}`}>
                          <AccordionTrigger className="text-left font-medium">
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-700">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-gray-600">No FAQ items match your search. Try different keywords.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
          <p className="text-gray-700 mb-8">Our friendly support team is here to help 24/7</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6 text-center">
                <h3 className="font-bold text-gray-900 mb-2">Live Chat</h3>
                <p className="text-gray-600 mb-4">Get instant help from our support team</p>
                <button className="bg-yellow-600 text-white px-6 py-2 rounded hover:bg-yellow-700">
                  Start Chat
                </button>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center">
                <h3 className="font-bold text-gray-900 mb-2">Email Support</h3>
                <p className="text-gray-600 mb-4">Send us your questions via email</p>
                <button className="bg-yellow-600 text-white px-6 py-2 rounded hover:bg-yellow-700">
                  Send Email
                </button>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center">
                <h3 className="font-bold text-gray-900 mb-2">Phone Support</h3>
                <p className="text-gray-600 mb-4">Call us at 1800 GOLDEN</p>
                <button className="bg-yellow-600 text-white px-6 py-2 rounded hover:bg-yellow-700">
                  Call Now
                </button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
